import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card,
  Button,
  Form,
  Spinner,
  Modal,
  ProgressBar,
} from "react-bootstrap";
import { Eye, Pencil, Copy } from "lucide-react";
import QuickQuizService, {
  QuizListItem,
} from "../../_services/QuickQuizService";
import "./quick-quiz-maker.css";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return `${String(d.getDate()).padStart(2, "0")}-${MONTHS[d.getMonth()]}-${d.getFullYear()}`;
};

/** "12-Mar-2026 09:00" — used for the start/end window shown under the test name. */
const formatDateTime = (dateStr?: string | null): string => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  const day = String(d.getDate()).padStart(2, "0");
  const mon = MONTHS[d.getMonth()];
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${day}-${mon}-${year} ${hh}:${mm}`;
};

const DOWNLOAD_BEFORE_OPTIONS = [
  { value: 0, label: "Anytime" },
  { value: 1, label: "Test StartTime" },
  { value: 2, label: "5 mins" },
  { value: 3, label: "10 mins" },
  { value: 4, label: "15 mins" },
  { value: 5, label: "30 mins" },
  { value: 6, label: "1 hr" },
  { value: 7, label: "2 hr" },
  { value: 8, label: "3 hr" },
  { value: 9, label: "1 day" },
  { value: 10, label: "1 week" },
];

interface QuizListPanelProps {
  onEdit: (testId: number) => void;
  onView: (testId: number) => void;
  onQuickEdit?: (testId: number) => void;
}

const QuizListPanel: React.FC<QuizListPanelProps> = ({ onEdit, onView, onQuickEdit }) => {
  const [quizzes, setQuizzes] = useState<QuizListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTestId, setSearchTestId] = useState("");
  const [searchCategoryId, setSearchCategoryId] = useState("");
  // New filters: test type, test-date (ISO YYYY-MM-DD), and academic year.
  // Year filter joins through test_year_mapping on the server side.
  const [searchTestTypeId, setSearchTestTypeId] = useState("");
  const [searchTestDate, setSearchTestDate] = useState("");
  const [searchYearId, setSearchYearId] = useState("");
  // Centre filter. "" = All Centres (only tests mapped to some centre),
  // "unassigned" = tests with no school/tag mapping, otherwise a school id.
  const [searchSchoolId, setSearchSchoolId] = useState("");
  const [categories, setCategories] = useState<Array<{ id: number; name: string }>>([]);
  const [testTypes, setTestTypes] = useState<Array<{ id: number; name: string; displayLabel?: string }>>([]);
  const [years, setYears] = useState<Array<{ id: number; name: string }>>([]);
  const [schools, setSchools] = useState<Array<{ id: number; name: string }>>([]);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 20,
    offset: 0,
    hasMore: false,
  });

  // Bundle modal state
  const [showBundleModal, setShowBundleModal] = useState(false);
  const [selectedTest, setSelectedTest] = useState<{
    testId: number;
    name: string;
  } | null>(null);
  const [downloadBefore, setDownloadBefore] = useState(0);
  const [bundling, setBundling] = useState(false);
  const [bundleResult, setBundleResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [bundleProgress, setBundleProgress] = useState<{
    status: string;
    progressPercentage: number;
    totalPapers: number;
    papersCompleted: number;
  } | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Clone modal state
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneSource, setCloneSource] = useState<{ testId: number; name: string; categoryName: string; totalQuestions: number } | null>(null);
  const [cloneForm, setCloneForm] = useState<{
    name: string; testDate: string; startDate: string; endDate: string;
    excludeQuestions: boolean; excludeCourseMappings: boolean;
  }>({ name: "", testDate: "", startDate: "", endDate: "", excludeQuestions: false, excludeCourseMappings: false });
  const [cloneSaving, setCloneSaving] = useState(false);
  const [cloneResult, setCloneResult] = useState<{ testId: number; name: string; totalQuestions: number } | null>(null);
  const [cloneError, setCloneError] = useState<string | null>(null);

  // Today's test statistics & monitoring
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [statsData, setStatsData] = useState<any>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [showMonitoringModal, setShowMonitoringModal] = useState(false);
  const [monitoringData, setMonitoringData] = useState<any>(null);
  const [monitoringLoading, setMonitoringLoading] = useState(false);
  const [monitoringDate, setMonitoringDate] = useState(new Date().toISOString().split("T")[0]);
  const [expandedTests, setExpandedTests] = useState<Set<number>>(new Set());
  const [monitoringView, setMonitoringView] = useState<"table" | "activity">("table");
  const [activityData, setActivityData] = useState<any>(null);
  const [activityLoading, setActivityLoading] = useState(false);
  // Student audit modals
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [auditData, setAuditData] = useState<any>(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditStudent, setAuditStudent] = useState<{ name: string; testName: string; testId: number; enquiryId: number }>({ name: "", testName: "", testId: 0, enquiryId: 0 });
  const [showDynamoModal, setShowDynamoModal] = useState(false);
  const [dynamoData, setDynamoData] = useState<any>(null);
  const [dynamoLoading, setDynamoLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const loadQuizzes = useCallback(
    async (offset = 0) => {
      setLoading(true);
      try {
        const result = await QuickQuizService.listQuizzes({
          q: searchQuery || undefined,
          testId: searchTestId ? Number(searchTestId) : undefined,
          categoryId: searchCategoryId ? Number(searchCategoryId) : undefined,
          testTypeId: searchTestTypeId ? Number(searchTestTypeId) : undefined,
          testDate: searchTestDate || undefined,
          yearId: searchYearId ? Number(searchYearId) : undefined,
          schoolId: searchSchoolId
            ? searchSchoolId === "unassigned"
              ? "unassigned"
              : Number(searchSchoolId)
            : undefined,
          limit: 20,
          offset,
        });
        setQuizzes(result.quizzes);
        setPagination(result.pagination);
      } catch (err: any) {
        console.error("Failed to load quizzes:", err);
      } finally {
        setLoading(false);
      }
    },
    [searchQuery, searchTestId, searchCategoryId, searchTestTypeId, searchTestDate, searchYearId, searchSchoolId]
  );

  useEffect(() => {
    // Load quizzes on initial mount
    QuickQuizService.listQuizzes({ limit: 20, offset: 0 })
      .then((result) => {
        setQuizzes(result.quizzes);
        setPagination(result.pagination);
      })
      .catch((err) => console.error("Failed to load quizzes:", err))
      .finally(() => setLoading(false));
    QuickQuizService.getLookups()
      .then((data) => {
        setCategories(data.categories || []);
        setTestTypes(data.testTypes || []);
        setYears(data.years || []);
        setSchools(data.schools || []);
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const handleSearch = () => {
    loadQuizzes(0);
  };

  const handlePrev = () => {
    const newOffset = Math.max(0, pagination.offset - pagination.limit);
    loadQuizzes(newOffset);
  };

  const handleNext = () => {
    loadQuizzes(pagination.offset + pagination.limit);
  };

  const openStudentAudit = async (testId: number, enquiryId: number, studentName: string, testName: string) => {
    setAuditStudent({ name: studentName, testName, testId, enquiryId });
    setAuditLoading(true); setAuditData(null); setShowAuditModal(true);
    try { const data = await QuickQuizService.getStudentAuditTrail(testId, enquiryId); setAuditData(data); }
    catch { /* ignore */ } finally { setAuditLoading(false); }
  };

  const openStudentDynamo = async (testId: number, enquiryId: number, studentName: string, testName: string) => {
    setAuditStudent({ name: studentName, testName, testId, enquiryId });
    setDynamoLoading(true); setDynamoData(null); setShowDynamoModal(true);
    try { const data = await QuickQuizService.getStudentDynamoResponses(testId, enquiryId); setDynamoData(data); }
    catch { /* ignore */ } finally { setDynamoLoading(false); }
  };

  const handleDyna2Rds = async () => {
    if (!auditStudent.testId) return;
    setActionLoading(true);
    try {
      const result = await QuickQuizService.dyna2Rds(auditStudent.testId, auditStudent.enquiryId);
      // eslint-disable-next-line no-alert
      window.alert(result.message || "Done");
    } catch (err: any) { window.alert(err?.response?.data?.message || "Failed"); } // eslint-disable-line no-alert
    finally { setActionLoading(false); }
  };

  const handleDeleteDynamo = async () => {
    if (!window.confirm("Are you sure you want to delete DynamoDB responses?")) return; // eslint-disable-line no-restricted-globals
    setActionLoading(true);
    try {
      const result = await QuickQuizService.deleteStudentDynamoResponses(auditStudent.testId, auditStudent.enquiryId);
      window.alert(result.message || "Deleted"); // eslint-disable-line no-alert
      setShowDynamoModal(false);
    } catch (err: any) { window.alert(err?.response?.data?.message || "Failed"); } // eslint-disable-line no-alert
    finally { setActionLoading(false); }
  };

  const handlePopulate = async (testId: number, enquiryId: number) => {
    if (!window.confirm("Populate marks from responses?")) return; // eslint-disable-line no-restricted-globals
    try {
      const result = await QuickQuizService.populateStudentData(testId, enquiryId);
      window.alert(result.message || "Done"); // eslint-disable-line no-alert
      loadTodayMonitoring();
    } catch (err: any) { window.alert(err?.response?.data?.message || "Failed"); } // eslint-disable-line no-alert
  };

  const loadActivityRoll = async (date?: string) => {
    setActivityLoading(true);
    try {
      const data = await QuickQuizService.getTodayActivityRoll(date || monitoringDate);
      setActivityData(data);
    } catch { setActivityData(null); }
    finally { setActivityLoading(false); }
  };

  const loadTodayStats = async () => {
    setStatsLoading(true);
    try {
      const data = await QuickQuizService.getTodayTestStatistics();
      setStatsData(data);
    } catch { setStatsData(null); }
    finally { setStatsLoading(false); }
  };

  const loadTodayMonitoring = async (date?: string) => {
    setMonitoringLoading(true);
    try {
      const data = await QuickQuizService.getTodayTestMonitoring(date || monitoringDate);
      setMonitoringData(data);
      // Expand all tests by default
      setExpandedTests(new Set((data?.tests || []).map((t: any) => t.testId)));
    } catch { setMonitoringData(null); }
    finally { setMonitoringLoading(false); }
  };

  const openCloneModal = (testId: number, name: string, categoryName: string, totalQuestions: number) => {
    setCloneSource({ testId, name, categoryName, totalQuestions });
    setCloneForm({ name: `${name} - Copy`, testDate: "", startDate: "", endDate: "", excludeQuestions: false, excludeCourseMappings: false });
    setCloneResult(null);
    setCloneError(null);
    setShowCloneModal(true);
  };

  const handleClone = async () => {
    if (!cloneSource) return;
    setCloneSaving(true);
    setCloneError(null);
    setCloneResult(null);
    try {
      const result = await QuickQuizService.cloneTest(cloneSource.testId, {
        name: cloneForm.name.trim() || undefined,
        testDate: cloneForm.testDate || undefined,
        startDate: cloneForm.startDate || undefined,
        endDate: cloneForm.endDate || undefined,
        excludeQuestions: cloneForm.excludeQuestions,
        excludeCourseMappings: cloneForm.excludeCourseMappings,
      });
      if (result.success && result.data) {
        setCloneResult({ testId: result.data.testId, name: result.data.name, totalQuestions: result.data.totalQuestions });
      } else {
        setCloneError(result.message || "Clone failed");
      }
    } catch (err: any) {
      setCloneError(err?.response?.data?.message || err.message || "Failed to clone test");
    } finally {
      setCloneSaving(false);
    }
  };

  const openBundleModal = (testId: number, name: string) => {
    setSelectedTest({ testId, name });
    setDownloadBefore(0);
    setBundleResult(null);
    setBundleProgress(null);
    setShowBundleModal(true);
  };

  const closeBundleModal = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setShowBundleModal(false);
    setBundling(false);
    setBundleResult(null);
    setBundleProgress(null);
    setSelectedTest(null);
  };

  const startPolling = (testId: number) => {
    if (pollRef.current) clearInterval(pollRef.current);

    pollRef.current = setInterval(async () => {
      try {
        const result = await QuickQuizService.getBundleStatus(testId);
        if (result.success && result.data) {
          setBundleProgress({
            status: result.data.status,
            progressPercentage: result.data.progressPercentage,
            totalPapers: result.data.totalPapers,
            papersCompleted: result.data.papersCompleted,
          });

          if (
            result.data.status === "COMPLETED" ||
            result.data.status === "FAILED"
          ) {
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = null;
            setBundling(false);

            if (result.data.status === "COMPLETED") {
              setBundleResult({
                success: true,
                message: "Bundle generation completed successfully.",
              });
            } else {
              setBundleResult({
                success: false,
                message:
                  result.data.errorMessage || "Bundle generation failed.",
              });
            }
          }
        }
      } catch (err) {
        console.error("Error polling bundle status:", err);
      }
    }, 3000);
  };

  const handleFinalizeBundle = async () => {
    if (!selectedTest) return;

    setBundling(true);
    setBundleResult(null);
    setBundleProgress(null);

    try {
      const result = await QuickQuizService.finalizeAndBundle(
        selectedTest.testId,
        downloadBefore
      );

      if (!result.success) {
        setBundling(false);
        setBundleResult({ success: false, message: result.message });
        return;
      }

      // Initial trigger succeeded — start polling for progress
      setBundleResult({ success: true, message: result.message });
      startPolling(selectedTest.testId);
    } catch (err: any) {
      setBundling(false);
      setBundleResult({
        success: false,
        message: err?.response?.data?.message || err.message || "Request failed",
      });
    }
  };

  return (
    <>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>Manage Tests</span>
          <div className="d-flex" style={{ gap: "4px" }}>
            <button className="btn"
              style={{ backgroundColor: "var(--status-danger)", color: "var(--text-on-brand)", border: "none", padding: "4px 12px", fontSize: "0.8rem", borderRadius: "4px", margin: "2px" }}
              onClick={() => { loadTodayMonitoring(); setShowMonitoringModal(true); }}>
              Today&apos;s Test Monitoring
            </button>
            <button className="btn"
              style={{ backgroundColor: "var(--status-success)", color: "var(--text-on-brand)", border: "none", padding: "4px 12px", fontSize: "0.8rem", borderRadius: "4px", margin: "2px" }}
              onClick={() => { loadTodayStats(); setShowStatsModal(true); }}>
              Today&apos;s Statistics
            </button>
          </div>
        </Card.Header>
        <Card.Body>
          {/* Filter bar — laid out with CSS Grid so every label sits in the
              same horizontal band and every control in the row below it.
              Mixing flex with Bootstrap's Form.Group margins caused the
              "Year" label to render inline on a tight row; the grid removes
              that ambiguity entirely. The Search button is its own column at
              the right edge that spans both rows so it's the height of the
              control + label. */}
          {(() => {
            const filterLabelStyle: React.CSSProperties = {
              fontSize: "0.7rem",
              fontWeight: 600,
              color: "var(--text-body)",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              marginBottom: 4,
              lineHeight: 1.2,
              whiteSpace: "nowrap",
            };
            const filterControlStyle: React.CSSProperties = {
              fontSize: "0.85rem",
              padding: "6px 8px",
              borderRadius: 6,
              border: "1px solid var(--border-strong)",
              background: "var(--surface-card)",
              width: "100%",
              height: 34,
              boxSizing: "border-box",
            };
            return (
              <div
                className="mb-3"
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "90px minmax(160px, 1fr) 160px 150px 140px 120px 150px auto",
                  gap: "6px 10px",
                  alignItems: "end",
                }}
              >
                <label style={filterLabelStyle}>Test ID</label>
                <label style={filterLabelStyle}>Test Name</label>
                <label style={filterLabelStyle}>Category</label>
                <label style={filterLabelStyle}>Test Type</label>
                <label style={filterLabelStyle}>Test Date</label>
                <label style={filterLabelStyle}>Year</label>
                <label style={filterLabelStyle}>Centre</label>
                <span aria-hidden="true" />

                <input
                  type="number"
                  placeholder="ID"
                  value={searchTestId}
                  onChange={(e) => setSearchTestId(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  style={filterControlStyle}
                />
                <input
                  type="text"
                  placeholder="Search by test name…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  style={filterControlStyle}
                />
                <select
                  value={searchCategoryId}
                  onChange={(e) => setSearchCategoryId(e.target.value)}
                  style={filterControlStyle}
                >
                  <option value="">All Categories</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <select
                  value={searchTestTypeId}
                  onChange={(e) => setSearchTestTypeId(e.target.value)}
                  style={filterControlStyle}
                >
                  <option value="">All Test Types</option>
                  {testTypes.map((t) => (
                    <option key={t.id} value={t.id}>{t.displayLabel || t.name}</option>
                  ))}
                </select>
                <input
                  type="date"
                  value={searchTestDate}
                  onChange={(e) => setSearchTestDate(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  style={filterControlStyle}
                />
                <select
                  value={searchYearId}
                  onChange={(e) => setSearchYearId(e.target.value)}
                  style={filterControlStyle}
                >
                  <option value="">All Years</option>
                  {years.map((y) => (
                    <option key={y.id} value={y.id}>{y.name}</option>
                  ))}
                </select>
                <select
                  value={searchSchoolId}
                  onChange={(e) => setSearchSchoolId(e.target.value)}
                  style={filterControlStyle}
                >
                  <option value="">All Centres</option>
                  <option value="unassigned">Unassigned</option>
                  {schools.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                <Button
                  variant="primary"
                  onClick={handleSearch}
                  disabled={loading}
                  size="sm"
                  style={{
                    backgroundColor: "var(--brand-primary)",
                    color: "var(--text-on-brand)",
                    border: "1px solid var(--brand-primary)",
                    height: 34,
                    padding: "0 18px",
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    borderRadius: 6,
                    whiteSpace: "nowrap",
                  }}
                >
                  {loading ? <Spinner role="status" animation="border" size="sm" /> : "Search"}
                </Button>
              </div>
            );
          })()}

          {loading && quizzes.length === 0 ? (
            <div className="text-center py-4">
              <Spinner role="status" animation="border" />
            </div>
          ) : quizzes.length === 0 ? (
            <p className="text-muted text-center py-3">No tests found.</p>
          ) : (
            <>
              <div style={{ fontSize: "0.85rem", overflowX: "auto" }}>
                <table className="table table-sm table-hover mb-0" style={{ tableLayout: "auto" }}>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Category</th>
                      <th>Type</th>
                      <th>Date</th>
                      <th>Qs</th>
                      <th>Marks</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {quizzes.map((q) => (
                      <tr key={q.testId}>
                        <td>{q.testId}</td>
                        <td
                          title={q.name}
                          style={{
                            maxWidth: "250px",
                          }}
                        >
                          <div
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {q.name}
                          </div>
                          {(q.startDate || q.endDate) && (
                            <div
                              style={{
                                fontSize: "0.7rem",
                                color: "var(--text-muted)",
                                fontFamily:
                                  "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                                marginTop: 2,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                              title={
                                (q.startDate ? `Start: ${formatDateTime(q.startDate)}` : "") +
                                (q.endDate ? ` | End: ${formatDateTime(q.endDate)}` : "")
                              }
                            >
                              {q.startDate ? formatDateTime(q.startDate) : "—"}
                              <span style={{ margin: "0 4px", color: "var(--text-faint)" }}>→</span>
                              {q.endDate ? formatDateTime(q.endDate) : "—"}
                            </div>
                          )}
                        </td>
                        <td>{q.categoryName || "-"}</td>
                        <td>{q.testTypeName || "-"}</td>
                        <td>{formatDate(q.testDate)}</td>
                        <td>{q.totalQuestions ?? "-"}</td>
                        <td>{q.totalMarks ?? "-"}</td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          <div className="qql-actions">
                            <button
                              type="button"
                              className="qql-action qql-action-view"
                              onClick={() => onView(q.testId)}
                              title="View"
                              aria-label="View"
                            >
                              <Eye size={15} />
                            </button>
                            <button
                              type="button"
                              className="qql-action qql-action-edit"
                              onClick={() => (onQuickEdit ?? onView)(q.testId)}
                              title="Edit"
                              aria-label="Edit"
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              type="button"
                              className="qql-action qql-action-clone"
                              onClick={() => openCloneModal(q.testId, q.name, q.categoryName || "", q.totalQuestions ?? 0)}
                              title="Clone"
                              aria-label="Clone"
                            >
                              <Copy size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-3">
                <small className="text-muted">
                  Showing {pagination.offset + 1}–
                  {Math.min(
                    pagination.offset + pagination.limit,
                    pagination.total
                  )}{" "}
                  of {pagination.total}
                </small>
                <div className="d-flex gap-2">
                  <button className="btn"
                    disabled={pagination.offset === 0}
                    onClick={handlePrev}
                    style={{ backgroundColor: "#6c757d", color: "var(--text-on-brand)", border: "none", margin: "2px", padding: "4px 12px", fontSize: "0.85rem", borderRadius: "4px", opacity: pagination.offset === 0 ? 0.5 : 1 }}>
                    Previous
                  </button>
                  <button className="btn"
                    disabled={!pagination.hasMore}
                    onClick={handleNext}
                    style={{ backgroundColor: "#6c757d", color: "var(--text-on-brand)", border: "none", margin: "2px", padding: "4px 12px", fontSize: "0.85rem", borderRadius: "4px", opacity: !pagination.hasMore ? 0.5 : 1 }}>
                    Next
                  </button>
                </div>
              </div>
            </>
          )}

        </Card.Body>
      </Card>

      {/* Today's Test Statistics Modal */}
      <Modal show={showStatsModal} onHide={() => setShowStatsModal(false)} centered size="lg" dialogClassName="qq-stats-modal">
        <style>{`
          .qq-stats-modal .modal-content { border: none; border-radius: 14px; overflow: hidden; box-shadow: 0 18px 50px rgba(0,0,0,0.22); }
          .qq-stats-head { background: var(--gradient-brand); color: #fff; padding: 16px 22px; border: none; }
          .qq-stats-head .modal-title { font-size: 1.08rem; font-weight: 700; letter-spacing: 0.2px; display: flex; align-items: center; gap: 9px; }
          .qq-stats-head .btn-close { filter: invert(1) grayscale(1) brightness(1.6); opacity: 0.9; }
          .qq-stats-body { background: #f4f6fb; padding: 20px 22px; max-height: 540px; overflow-y: auto; }
          .qq-cat-card { background: #fff; border-radius: 12px; box-shadow: 0 2px 10px rgba(15,23,42,0.06); overflow: hidden; margin-bottom: 18px; }
          .qq-cat-title { display: flex; align-items: center; gap: 8px; font-size: 0.92rem; font-weight: 700; color: #1e293b; padding: 12px 16px; border-bottom: 1px solid #eef1f6; }
          .qq-cat-dot { width: 9px; height: 9px; border-radius: 50%; background: #7c3aed; box-shadow: 0 0 0 4px rgba(124,58,237,0.14); }
          .qq-tbl { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 0.82rem; }
          .qq-tbl thead th { background: #f8fafc; color: #64748b; font-weight: 600; text-transform: uppercase; font-size: 0.68rem; letter-spacing: 0.5px; padding: 9px 12px; border-bottom: 1px solid #e9edf3; text-align: center; white-space: nowrap; }
          .qq-tbl thead th.qq-left { text-align: left; }
          .qq-tbl tbody td { padding: 10px 12px; border-bottom: 1px solid #f1f4f8; color: #334155; vertical-align: middle; text-align: center; }
          .qq-tbl tbody tr:last-child td { border-bottom: none; }
          .qq-tbl tbody tr:hover td { background: #fafbff; }
          .qq-tbl td.qq-left { text-align: left; }
          .qq-test-name { font-weight: 600; color: #1e293b; }
          .qq-test-dates { display: inline-flex; align-items: center; gap: 5px; margin-top: 4px; font-size: 0.72rem; font-weight: 600; color: #059669; background: #ecfdf5; border-radius: 6px; padding: 2px 8px; }
          .qq-idx { color: #94a3b8; font-weight: 600; }
          .qq-tid { font-weight: 600; color: #6366f1; }
          .qq-pill { display: inline-block; min-width: 34px; padding: 3px 9px; border-radius: 999px; font-weight: 700; font-size: 0.76rem; }
          .qq-pill-prog { background: #fef3c7; color: #b45309; }
          .qq-pill-done { background: #dcfce7; color: #15803d; }
          .qq-pill-total { background: #e0e7ff; color: #4338ca; }
          .qq-pill-zero { background: #f1f5f9; color: #94a3b8; }
          .qq-foot-row td { background: #f8fafc; font-weight: 700; color: #1e293b; padding: 10px 12px; border-top: 2px solid #e9edf3; text-align: center; }
          .qq-foot-row td.qq-left { text-align: right; color: #64748b; text-transform: uppercase; font-size: 0.7rem; letter-spacing: 0.5px; }
          .qq-overall { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
          .qq-ov-card { background: #fff; border-radius: 12px; padding: 14px 12px; text-align: center; box-shadow: 0 2px 10px rgba(15,23,42,0.06); border-top: 3px solid #cbd5e1; }
          .qq-ov-card.qq-ov-tests { border-top-color: #6366f1; }
          .qq-ov-card.qq-ov-prog { border-top-color: #f59e0b; }
          .qq-ov-card.qq-ov-done { border-top-color: #22c55e; }
          .qq-ov-card.qq-ov-total { border-top-color: #0ea5e9; }
          .qq-ov-num { font-size: 1.5rem; font-weight: 800; color: #1e293b; line-height: 1; }
          .qq-ov-label { margin-top: 6px; font-size: 0.7rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: #94a3b8; }
          .qq-overall-title { font-size: 0.78rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.6px; color: #64748b; margin: 4px 0 12px; }
          .qq-stats-foot { background: #fff; border-top: 1px solid #eef1f6; padding: 12px 18px; }
          .qq-btn { border: none; padding: 7px 18px; font-size: 0.82rem; font-weight: 600; border-radius: 8px; color: #fff; transition: opacity 0.15s; }
          .qq-btn:hover { opacity: 0.9; }
          .qq-btn-close { background: #64748b; }
          .qq-btn-refresh { background: var(--gradient-brand); }
        `}</style>
        <Modal.Header closeButton className="qq-stats-head">
          <Modal.Title>📊 Today Test Statistics</Modal.Title>
        </Modal.Header>
        <Modal.Body className="qq-stats-body">
          {statsLoading ? (
            <div className="text-center py-4"><Spinner role="status" animation="border" size="sm" /> Loading...</div>
          ) : statsData?.categories?.length > 0 ? (
            <>
              <div className="qq-overall-title">Overall Summary</div>
              <div className="qq-overall" style={{ marginBottom: 18 }}>
                <div className="qq-ov-card qq-ov-tests"><div className="qq-ov-num">{statsData.overall.testCount}</div><div className="qq-ov-label"># Tests</div></div>
                <div className="qq-ov-card qq-ov-prog"><div className="qq-ov-num">{statsData.overall.inProgress}</div><div className="qq-ov-label">In Progress</div></div>
                <div className="qq-ov-card qq-ov-done"><div className="qq-ov-num">{statsData.overall.completed}</div><div className="qq-ov-label">Completed</div></div>
                <div className="qq-ov-card qq-ov-total"><div className="qq-ov-num">{statsData.overall.total}</div><div className="qq-ov-label">Total</div></div>
              </div>
              {statsData.categories.map((cat: any) => {
                const sumProg = cat.tests.reduce((s: number, t: any) => s + t.inProgress, 0);
                const sumDone = cat.tests.reduce((s: number, t: any) => s + t.completed, 0);
                const sumTotal = cat.tests.reduce((s: number, t: any) => s + t.total, 0);
                return (
                  <div key={cat.categoryId} className="qq-cat-card">
                    <div className="qq-cat-title"><span className="qq-cat-dot" />{cat.categoryName}</div>
                    <table className="qq-tbl">
                      <thead>
                        <tr><th>#</th><th>Test Id</th><th className="qq-left">Test Name</th><th>In Progress</th><th>Completed</th><th>Total</th></tr>
                      </thead>
                      <tbody>
                        {cat.tests.map((t: any, idx: number) => (
                          <tr key={t.testId}>
                            <td className="qq-idx">{idx + 1}</td>
                            <td className="qq-tid">{t.testId}</td>
                            <td className="qq-left">
                              <div className="qq-test-name">{t.testName}</div>
                              {(t.startDate || t.endDate) && (
                                <div className="qq-test-dates">
                                  🕒 {t.startDate}{t.startDate && t.endDate ? " → " : ""}{t.endDate}
                                </div>
                              )}
                            </td>
                            <td><span className={`qq-pill ${t.inProgress ? "qq-pill-prog" : "qq-pill-zero"}`}>{t.inProgress}</span></td>
                            <td><span className={`qq-pill ${t.completed ? "qq-pill-done" : "qq-pill-zero"}`}>{t.completed}</span></td>
                            <td><span className="qq-pill qq-pill-total">{t.total}</span></td>
                          </tr>
                        ))}
                        <tr className="qq-foot-row">
                          <td colSpan={3} className="qq-left">Total</td>
                          <td>{sumProg}</td>
                          <td>{sumDone}</td>
                          <td>{sumTotal}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </>
          ) : (
            <div className="text-center text-muted py-4">📭 No tests found for today</div>
          )}
        </Modal.Body>
        <Modal.Footer className="qq-stats-foot">
          <button className="qq-btn qq-btn-close" onClick={() => setShowStatsModal(false)}>Close</button>
          <button className="qq-btn qq-btn-refresh" onClick={() => loadTodayStats()}>↻ Refresh</button>
        </Modal.Footer>
      </Modal>

      {/* Today's Test Monitoring Modal */}
      {showMonitoringModal && <style>{`.monitoring-fullscreen .modal-dialog { max-width: 100vw !important; width: 100vw !important; height: 100vh !important; margin: 0 !important; } .monitoring-fullscreen .modal-content { height: 100vh !important; border: none !important; border-radius: 0 !important; }`}</style>}
      <Modal show={showMonitoringModal} onHide={() => setShowMonitoringModal(false)} className="monitoring-fullscreen">
        <Modal.Header style={{ backgroundColor: "var(--status-success)", color: "var(--text-on-brand)", padding: "10px 16px" }}>
          <Modal.Title style={{ fontSize: "1rem" }}>
            Today&apos;s Test Monitoring - {monitoringData?.date || monitoringDate}
            <span className="ms-3" style={{ fontSize: "0.85rem", fontWeight: 400 }}>
              # Students: {monitoringData?.totalStudents || 0}
            </span>
          </Modal.Title>
          <div className="ms-auto d-flex align-items-center" style={{ gap: "4px" }}>
            <button className="btn btn-sm" onClick={() => { setMonitoringView("table"); loadTodayMonitoring(); }}
              style={{ backgroundColor: "var(--status-warning)", color: "var(--text-strong)", border: "none", padding: "3px 10px", fontSize: "0.8rem", borderRadius: "3px", margin: "2px" }}>
              Refresh &#8634;
            </button>
            <button className="btn btn-sm" onClick={() => window.print()}
              style={{ backgroundColor: "var(--status-success)", color: "var(--text-on-brand)", border: "none", padding: "3px 10px", fontSize: "0.8rem", borderRadius: "3px", margin: "2px" }}>
              Print &#128424;
            </button>
            <button className="btn btn-sm" onClick={() => { setMonitoringView("activity"); loadActivityRoll(); }}
              style={{ backgroundColor: "#ff8c00", color: "var(--text-on-brand)", border: "none", padding: "3px 10px", fontSize: "0.8rem", borderRadius: "3px", margin: "2px" }}>
              Activity &#128249;
            </button>
            <input type="date" className="form-control form-control-sm" value={monitoringDate}
              onChange={(e) => { setMonitoringDate(e.target.value); if (monitoringView === "activity") loadActivityRoll(e.target.value); else loadTodayMonitoring(e.target.value); }}
              style={{ width: "140px", fontSize: "0.8rem", margin: "2px" }} />
            <button className="btn btn-sm" onClick={() => { if (monitoringView === "activity") loadActivityRoll(); else loadTodayMonitoring(); }}
              style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "none", padding: "3px 8px", fontSize: "0.8rem", borderRadius: "3px", margin: "2px" }}>
              &#128269;
            </button>
            <button className="btn btn-sm" onClick={() => setShowMonitoringModal(false)}
              style={{ backgroundColor: "var(--surface-card)", color: "var(--text-strong)", border: "none", padding: "3px 8px", fontSize: "1rem", borderRadius: "3px", margin: "2px", lineHeight: 1 }}>
              &times;
            </button>
          </div>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "0.82rem", overflowY: "auto", padding: "12px" }}>
          {monitoringView === "activity" ? (
            /* Activity Roll View — proctoring filmstrip */
            activityLoading ? (
              <div className="text-center py-5"><Spinner role="status" animation="border" /> Loading activity...</div>
            ) : activityData?.tests?.length > 0 ? (
              activityData.tests.map((test: any) => (
                <div key={test.testId} className="mb-4">
                  <div style={{ backgroundColor: "#2196F3", color: "var(--text-on-brand)", padding: "8px 12px", borderRadius: "4px", fontSize: "0.8rem", fontWeight: 600 }}>
                    Test: {test.testId} | {test.testName}
                  </div>
                  {test.students.map((s: any, idx: number) => (
                    <div key={`${s.enquiryId}-${idx}`} className="d-flex mb-2 mt-2" style={{ border: "1px solid var(--border-default)", borderRadius: "4px", overflow: "hidden" }}>
                      <div style={{ backgroundColor: "#20b2aa", color: "var(--text-on-brand)", minWidth: "180px", padding: "8px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontWeight: 600, fontSize: "0.8rem", textAlign: "center" }}>
                        {s.photoUrl ? (
                          <img src={s.photoUrl} alt={s.studentName}
                            style={{ width: "60px", height: "60px", borderRadius: "50%", objectFit: "cover", border: "2px solid #fff", marginBottom: "4px" }} />
                        ) : (
                          <div style={{ width: "60px", height: "60px", borderRadius: "50%", backgroundColor: "#178a7a", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.2rem", border: "2px solid #fff", marginBottom: "4px" }}>
                            {s.studentName?.charAt(0) || "?"}
                          </div>
                        )}
                        {s.studentName}
                      </div>
                      <div style={{
                        flex: 1, padding: "8px", background: "#c0c0c0",
                        backgroundImage: "repeating-linear-gradient(90deg, transparent, transparent 15px, #888 15px, #888 17px)",
                        borderTop: "8px solid #333", borderBottom: "8px solid #333",
                        display: "flex", alignItems: "center", overflowX: "auto", gap: "4px",
                      }}>
                        {s.images && s.images.length > 0 ? (
                          s.images.map((imgUrl: string, i: number) => (
                            <img key={i} src={imgUrl} alt={`Proctor ${i + 1}`}
                              style={{ width: "80px", height: "60px", objectFit: "cover", border: "1px solid #fff", borderRadius: "2px" }} />
                          ))
                        ) : (
                          <span style={{ color: "var(--text-body)", fontStyle: "italic", padding: "10px" }}>No records found !!</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))
            ) : (
              <p className="text-muted text-center py-5">No activity data for {monitoringDate}</p>
            )
          ) : monitoringLoading ? (
            <div className="text-center py-5"><Spinner role="status" animation="border" /> Loading...</div>
          ) : monitoringData?.tests?.length > 0 ? (
            monitoringData.tests.map((test: any) => (
              <div key={test.testId} className="mb-4">
                <div style={{ backgroundColor: "#2196F3", color: "var(--text-on-brand)", padding: "8px 12px", borderRadius: "4px 4px 0 0", fontSize: "0.8rem", cursor: "pointer" }}
                  onClick={() => setExpandedTests((prev) => { const n = new Set(prev); n.has(test.testId) ? n.delete(test.testId) : n.add(test.testId); return n; })}>
                  <span style={{ fontWeight: 600 }}>Test: {test.testId} | {test.testName}</span>
                  <span style={{ marginLeft: "16px" }}># All Students</span>
                  <span style={{ marginLeft: "16px" }}># Total Students: <strong>{test.totalStudents}</strong></span>
                  <span style={{ marginLeft: "16px" }}># Test Taken: <strong>{test.completed + test.inProgress}</strong></span>
                  <span style={{ marginLeft: "16px" }}># Completed: <strong>{test.completed}</strong></span>
                  <span style={{ marginLeft: "16px" }}># In Progress: <strong>{test.inProgress}</strong></span>
                  <span style={{ marginLeft: "16px" }}>Class Performance (AVG): <strong>{test.classPerformanceAvg}</strong></span>
                  <span style={{ marginLeft: "16px" }}>Test Performance (AVG): <strong>{test.testPerformanceAvg}</strong></span>
                  <span className="float-end">{expandedTests.has(test.testId) ? "▲" : "▼"}</span>
                </div>
                {expandedTests.has(test.testId) && (
                  <div style={{ overflowX: "auto" }}>
                    <table className="table table-sm table-bordered mb-0" style={{ fontSize: "0.78rem" }}>
                      <thead className="table-light">
                        <tr>
                          <th style={{ padding: "6px 8px" }}>S.No.</th>
                          <th style={{ padding: "6px 8px" }}>Student Name</th>
                          <th style={{ padding: "6px 8px" }}>Student Code</th>
                          <th style={{ padding: "6px 8px" }}>Mobile</th>
                          <th style={{ padding: "6px 8px" }}>Section</th>
                          <th style={{ padding: "6px 8px" }}>Centre</th>
                          <th style={{ padding: "6px 8px" }}>Course Code</th>
                          <th style={{ padding: "6px 8px" }}>Status</th>
                          <th style={{ padding: "6px 8px" }}>Start Time</th>
                          <th style={{ padding: "6px 8px" }}>End Time</th>
                          <th style={{ padding: "6px 8px" }}>Audit</th>
                          <th style={{ padding: "6px 8px" }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {test.students.map((s: any, idx: number) => (
                          <tr key={`${s.enquiryId}-${idx}`}>
                            <td style={{ padding: "6px 8px" }}>{idx + 1}</td>
                            <td style={{ padding: "6px 8px" }}>{s.studentName}</td>
                            <td style={{ padding: "6px 8px" }}>{s.studentCode}</td>
                            <td style={{ padding: "6px 8px" }}>{s.mobile}</td>
                            <td style={{ padding: "6px 8px" }}>{s.batchName}</td>
                            <td style={{ padding: "6px 8px" }}>{s.centreName}</td>
                            <td style={{ padding: "6px 8px" }}>{s.courseCode}</td>
                            <td style={{ padding: "6px 8px", color: s.status === "Completed" ? "#28a745" : "#ffc107", fontWeight: 600 }}>{s.status}</td>
                            <td style={{ padding: "6px 8px" }}>{s.startTime}</td>
                            <td style={{ padding: "6px 8px" }}>{s.endTime}</td>
                            <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                              <button className="btn btn-sm" title="Info" onClick={() => openStudentAudit(test.testId, s.enquiryId, s.studentName, test.testName)}
                                style={{ fontSize: "0.85rem", padding: "2px 4px", color: "var(--brand-primary)", border: "none", background: "none", margin: "2px" }}>&#9432;</button>
                              <button className="btn btn-sm" title="DynamoDB" onClick={() => openStudentDynamo(test.testId, s.enquiryId, s.studentName, test.testName)}
                                style={{ fontSize: "0.85rem", padding: "2px 4px", color: "var(--brand-primary)", border: "none", background: "none", margin: "2px" }}>&#128451;</button>
                              {s.status === "Completed" && (
                                <button className="btn btn-sm" title="Populate" onClick={() => handlePopulate(test.testId, s.enquiryId)}
                                  style={{ fontSize: "0.85rem", padding: "2px 4px", color: "var(--status-success)", border: "none", background: "none", margin: "2px" }}>&#128450;</button>
                              )}
                            </td>
                            <td style={{ padding: "6px 8px" }}>
                              {s.status === "Completed" && (
                                <button className="btn btn-sm" title="Delete" onClick={() => handlePopulate(test.testId, s.enquiryId)}
                                  style={{ fontSize: "0.85rem", padding: "2px 4px", color: "var(--status-danger)", border: "none", background: "none", margin: "2px" }}>&#10005;</button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))
          ) : (
            <p className="text-muted text-center py-5">No test monitoring data for {monitoringDate}</p>
          )}
        </Modal.Body>
      </Modal>

      {/* Finalize & Bundle Modal */}
      <Modal
        show={showBundleModal}
        onHide={closeBundleModal}
        centered
        backdrop={bundling ? "static" : true}
        keyboard={!bundling}
      >
        <Modal.Header closeButton={!bundling}>
          <Modal.Title style={{ fontSize: "1.1rem" }}>
            Finalize & Bundle
            {selectedTest ? ` — ${selectedTest.name}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Download Before Dropdown */}
          {!bundleProgress && !bundleResult?.success && (
            <Form.Group className="mb-3">
              <Form.Label>Allow Test Download Before</Form.Label>
              <Form.Select
                value={downloadBefore}
                onChange={(e) => setDownloadBefore(Number(e.target.value))}
                disabled={bundling}
              >
                {DOWNLOAD_BEFORE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          {/* Progress Bar */}
          {bundling && bundleProgress && (
            <div className="mb-3">
              <ProgressBar
                now={bundleProgress.progressPercentage}
                label={`${bundleProgress.progressPercentage}%`}
                animated
                striped
              />
              <small className="text-muted d-block mt-1">
                Generating PDFs... {bundleProgress.papersCompleted} of{" "}
                {bundleProgress.totalPapers} papers complete
              </small>
            </div>
          )}

          {/* Bundling spinner (before progress starts) */}
          {bundling && !bundleProgress && (
            <div className="text-center py-3">
              <Spinner role="status" animation="border" size="sm" className="me-2" />
              <span>Processing... please wait</span>
            </div>
          )}

          {/* Result Message */}
          {bundleResult && !bundling && (
            <div
              style={{
                color: bundleResult.success ? "#28a745" : "#dc3545",
                fontWeight: 500,
              }}
            >
              {bundleResult.success
                ? "Bundle complete!"
                : `Error: ${bundleResult.message}`}
            </div>
          )}

          {/* Initial trigger success message (while still polling) */}
          {bundleResult?.success && bundling && (
            <small className="text-muted d-block mt-1">
              {bundleResult.message}
            </small>
          )}
        </Modal.Body>
        <Modal.Footer>
          {!bundling && !bundleResult?.success ? (
            <>
              <Button variant="secondary" onClick={closeBundleModal} style={{ backgroundColor: "#6c757d", color: "var(--text-on-brand)", border: "1px solid #6c757d", margin: "2px" }}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleFinalizeBundle}
                disabled={bundling}
                style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "1px solid var(--brand-primary)", margin: "2px" }}
              >
                Finalize & Bundle
              </Button>
            </>
          ) : !bundling && bundleResult?.success ? (
            <Button variant="primary" onClick={closeBundleModal} style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "1px solid var(--brand-primary)", margin: "2px" }}>
              Close
            </Button>
          ) : null}
        </Modal.Footer>
      </Modal>

      {/* Student Audit Trail Modal */}
      <Modal show={showAuditModal} onHide={() => setShowAuditModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "0.95rem" }}>
            {auditStudent.name} || {auditStudent.testName}[{auditStudent.testId}] Audit
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "0.82rem", maxHeight: "500px", overflowY: "auto" }}>
          {auditLoading ? (
            <div className="text-center py-3"><Spinner role="status" animation="border" size="sm" /> Loading...</div>
          ) : auditData ? (
            <>
              <p style={{ fontWeight: 600 }}>
                OS: {auditData.deviceInfo?.os} | {auditData.deviceInfo?.browser} | {auditData.deviceInfo?.ip}[IP]
              </p>
              {auditData.userActions?.length > 0 && (
                <table className="table table-sm table-bordered mb-3">
                  <thead className="table-light">
                    <tr><th>Action</th><th>Timestamp</th></tr>
                  </thead>
                  <tbody>
                    {auditData.userActions.map((a: any, i: number) => (
                      <tr key={i}>
                        <td style={{ fontWeight: a.action.includes("Started") || a.action.includes("Submitted") || a.action.includes("Loaded") ? 700 : 400 }}>{a.action}</td>
                        <td>{a.timestamp}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {auditData.dynamoSubmitStatus && (
                <p style={{ color: "var(--status-danger)", fontSize: "0.8rem" }}>{auditData.dynamoSubmitStatus}</p>
              )}
              {auditData.rdsSubmitStatus && (
                <p style={{ fontSize: "0.8rem" }}>{auditData.rdsSubmitStatus}</p>
              )}
            </>
          ) : <p className="text-muted">No audit data found</p>}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn" onClick={() => setShowAuditModal(false)}
            style={{ backgroundColor: "#6c757d", color: "var(--text-on-brand)", border: "none", padding: "4px 12px", fontSize: "0.85rem", borderRadius: "4px" }}>Close</button>
        </Modal.Footer>
      </Modal>

      {/* Student DynamoDB Responses Modal */}
      <Modal show={showDynamoModal} onHide={() => setShowDynamoModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "0.95rem" }}>
            {auditStudent.name} || {auditStudent.testName}[{auditStudent.testId}] Audit
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "0.82rem", maxHeight: "500px", overflowY: "auto" }}>
          {dynamoLoading ? (
            <div className="text-center py-3"><Spinner role="status" animation="border" size="sm" /> Loading...</div>
          ) : dynamoData ? (
            <>
              <h6 style={{ fontWeight: 700, textDecoration: "underline" }}>Test Responses</h6>
              {dynamoData.responses?.length > 0 ? (
                <table className="table table-sm table-bordered mb-3">
                  <thead className="table-light">
                    <tr><th>Section</th><th>Question</th><th>Attempted Choice</th><th>Attempt Choice String</th><th>Timestamp</th><th>Time Spent</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {dynamoData.responses.map((r: any, i: number) => (
                      <tr key={i}>
                        <td>{r.sectionId || r.section}</td>
                        <td>{r.questionId || r.question}</td>
                        <td>{r.attemptedChoice || r.choice || ""}</td>
                        <td>{r.attemptChoiceString || r.choiceString || ""}</td>
                        <td>{r.timestamp}</td>
                        <td>{r.timeSpent}</td>
                        <td>{r.status || "A"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="text-muted">No responses found</p>}
              {dynamoData.userDetails && (
                <>
                  <h6 style={{ fontWeight: 700, textDecoration: "underline" }}>User Details</h6>
                  <p><strong>Timer:</strong> {dynamoData.userDetails.timer || "-"}</p>
                  <p><strong>Uploads:</strong> {dynamoData.userDetails.uploads || "-"}</p>
                </>
              )}
            </>
          ) : <p className="text-muted">No DynamoDB data found</p>}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn" onClick={handleDyna2Rds} disabled={actionLoading}
            style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "none", padding: "4px 12px", fontSize: "0.85rem", borderRadius: "4px" }}>
            {actionLoading ? "Processing..." : "Dyna >>> RDS"}
          </button>
          <button className="btn" onClick={handleDeleteDynamo} disabled={actionLoading}
            style={{ backgroundColor: "var(--status-danger)", color: "var(--text-on-brand)", border: "none", padding: "4px 12px", fontSize: "0.85rem", borderRadius: "4px" }}>
            Delete Responses
          </button>
          <button className="btn" onClick={() => setShowDynamoModal(false)}
            style={{ backgroundColor: "#6c757d", color: "var(--text-on-brand)", border: "none", padding: "4px 12px", fontSize: "0.85rem", borderRadius: "4px" }}>Close</button>
        </Modal.Footer>
      </Modal>

      {/* Clone Test Modal */}
      <Modal show={showCloneModal} onHide={() => setShowCloneModal(false)} centered>
        <Modal.Header closeButton style={{ background: "linear-gradient(135deg, #6f42c1 0%, var(--brand-accent) 100%)", color: "var(--text-on-brand)" }}>
          <Modal.Title style={{ fontSize: "1rem", fontWeight: 700 }}>Clone to a New Test</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {cloneSource && (
            <div style={{
              background: "linear-gradient(135deg, var(--brand-primary-subtle) 0%, var(--brand-primary-subtle) 100%)",
              border: "1px solid var(--brand-primary-subtle)", borderRadius: 10, padding: "12px 16px", marginBottom: 16, fontSize: "0.84rem",
            }}>
              <div style={{ display: "flex", gap: 16 }}>
                <span><span style={{ color: "var(--text-muted)", fontWeight: 500 }}>Test Id</span> <strong>{cloneSource.testId}</strong></span>
                <span><span style={{ color: "var(--text-muted)", fontWeight: 500 }}>Test Name</span> <strong>{cloneSource.name}</strong></span>
              </div>
              <div style={{ marginTop: 6, fontSize: "0.78rem", color: "var(--text-muted)" }}>
                {cloneSource.categoryName && <span style={{ marginRight: 12 }}>{cloneSource.categoryName}</span>}
                <span>{cloneSource.totalQuestions} questions</span>
              </div>
            </div>
          )}

          {cloneResult && (
            <div style={{
              background: "var(--status-success-bg)", border: "1px solid #86efac", borderRadius: 8,
              padding: "12px 16px", marginBottom: 16, fontSize: "0.84rem", color: "var(--status-success-text)",
            }}>
              <strong>Cloned successfully!</strong>
              <div style={{ marginTop: 4 }}>
                New Test ID: <strong>{cloneResult.testId}</strong> — {cloneResult.name}
                {!cloneForm.excludeQuestions && <span> ({cloneResult.totalQuestions} questions)</span>}
              </div>
              <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                <button className="btn"
                  style={{ padding: "4px 12px", borderRadius: 6, fontSize: "0.78rem", fontWeight: 600, background: "var(--brand-primary)", color: "var(--text-on-brand)", border: "none" }}
                  onClick={() => { setShowCloneModal(false); onView(cloneResult.testId); }}>
                  View Cloned Test
                </button>
                <button className="btn"
                  style={{ padding: "4px 12px", borderRadius: 6, fontSize: "0.78rem", fontWeight: 600, background: "var(--surface-card)", color: "var(--brand-primary)", border: "1px solid var(--brand-primary)" }}
                  onClick={() => { setShowCloneModal(false); onEdit(cloneResult.testId); }}>
                  Edit Cloned Test
                </button>
              </div>
            </div>
          )}

          {cloneError && (
            <div style={{
              background: "var(--status-danger-bg)", border: "1px solid var(--status-danger-border)", borderRadius: 8,
              padding: "10px 14px", marginBottom: 14, fontSize: "0.82rem", color: "var(--status-danger)",
            }}>
              {cloneError}
            </div>
          )}

          {!cloneResult && (
            <>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontWeight: 600, fontSize: "0.84rem", marginBottom: 4 }}>Test Name</label>
                <input
                  type="text"
                  value={cloneForm.name}
                  onChange={(e) => setCloneForm({ ...cloneForm, name: e.target.value })}
                  style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1.5px solid var(--border-default)", fontSize: "0.88rem" }}
                  placeholder="New test name"
                />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontWeight: 600, fontSize: "0.84rem", marginBottom: 4 }}>Test Date</label>
                <input
                  type="date"
                  value={cloneForm.testDate}
                  onChange={(e) => setCloneForm({ ...cloneForm, testDate: e.target.value })}
                  style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1.5px solid var(--border-default)", fontSize: "0.88rem" }}
                />
              </div>
              <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: "block", fontWeight: 600, fontSize: "0.84rem", marginBottom: 4 }}>Start Date</label>
                  <input
                    type="datetime-local"
                    value={cloneForm.startDate}
                    onChange={(e) => setCloneForm({ ...cloneForm, startDate: e.target.value })}
                    style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1.5px solid var(--border-default)", fontSize: "0.84rem" }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: "block", fontWeight: 600, fontSize: "0.84rem", marginBottom: 4 }}>End Date</label>
                  <input
                    type="datetime-local"
                    value={cloneForm.endDate}
                    onChange={(e) => setCloneForm({ ...cloneForm, endDate: e.target.value })}
                    style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1.5px solid var(--border-default)", fontSize: "0.84rem" }}
                  />
                </div>
              </div>
              <div style={{
                padding: "10px 14px", background: "var(--surface-page)", borderRadius: 8,
                border: "1px solid var(--border-default)", marginBottom: 8,
              }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", margin: 0, fontSize: "0.84rem", fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={cloneForm.excludeQuestions}
                    onChange={(e) => setCloneForm({ ...cloneForm, excludeQuestions: e.target.checked })}
                    style={{ width: 18, height: 18, accentColor: "#6f42c1", cursor: "pointer" }}
                  />
                  Exclude Questions
                </label>
                <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: 4, paddingLeft: 26 }}>
                  Clone only test metadata. No sections or questions will be copied.
                </div>
              </div>
              <div style={{
                padding: "10px 14px", background: "var(--surface-page)", borderRadius: 8,
                border: "1px solid var(--border-default)",
              }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", margin: 0, fontSize: "0.84rem", fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={cloneForm.excludeCourseMappings}
                    onChange={(e) => setCloneForm({ ...cloneForm, excludeCourseMappings: e.target.checked })}
                    style={{ width: 18, height: 18, accentColor: "#6f42c1", cursor: "pointer" }}
                  />
                  Exclude Course Mappings
                </label>
                <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: 4, paddingLeft: 26 }}>
                  Skip copying the source test's course / program / batch / term mappings into the cloned test.
                </div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm"
            style={{ backgroundColor: "#6c757d", color: "var(--text-on-brand)", border: "1px solid #6c757d", margin: "2px" }}
            onClick={() => { setShowCloneModal(false); if (cloneResult) loadQuizzes(pagination.offset); }}>
            {cloneResult ? "Close" : "Cancel"}
          </Button>
          {!cloneResult && (
            <Button variant="primary" size="sm"
              style={{ backgroundColor: "#6f42c1", color: "var(--text-on-brand)", border: "1px solid #6f42c1", margin: "2px" }}
              onClick={handleClone}
              disabled={cloneSaving || !cloneForm.name.trim()}>
              {cloneSaving ? <><Spinner role="status" animation="border" size="sm" className="me-1" />Cloning...</> : "Clone Test"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default QuizListPanel;
